/**
 * HTTP-клиент админки.
 *
 * Тот же backend, что у игрового фронтенда, и тот же механизм авторизации:
 * access-токен в памяти вкладки, refresh — в httpOnly-cookie. Разница только
 * в том, что админка обращается к методам под /api/v1/admin/..., доступ к
 * которым backend открывает по роли moderator или admin.
 */

export const API_URL = (
  import.meta.env.VITE_API_URL ?? "http://localhost:8080"
).replace(/\/$/, "");

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

let refreshPromise: Promise<boolean> | null = null;

/** Обновление access-токена по refresh-cookie; параллельные вызовы ждут один запрос. */
export async function refreshAccessToken(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      if (!response.ok) return false;

      const data = (await response.json()) as { accessToken?: string };
      if (!data.accessToken) return false;

      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      queueMicrotask(() => {
        refreshPromise = null;
      });
    }
  })();

  return refreshPromise;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  skipRefresh?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, skipRefresh = false } = options;

  const url = new URL(`${API_URL}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(url.toString(), {
    method,
    headers,
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 401 && !skipRefresh) {
    if (await refreshAccessToken()) {
      return request<T>(path, { ...options, skipRefresh: true });
    }
    accessToken = null;
  }

  if (!response.ok) {
    let code = "Unknown";
    let message = `Ошибка ${response.status}`;
    try {
      const data = (await response.json()) as { code?: string; message?: string };
      code = data.code ?? code;
      message = data.message ?? message;
    } catch {
      // Тело не JSON — оставляем сообщение по умолчанию.
    }
    throw new ApiError(response.status, code, message);
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * Загрузка изображения: presigned-ссылка от backend'а, затем файл уходит
 * напрямую в MinIO. Возвращается ключ объекта, который сохраняется в БД.
 */
export async function uploadImage(file: File): Promise<string> {
  const presigned = await request<{
    objectKey?: string;
    uploadUrl?: string;
  }>("/api/v1/media/uploads", {
    method: "POST",
    body: { contentType: file.type, sizeBytes: file.size },
  });

  if (!presigned.uploadUrl || !presigned.objectKey) {
    throw new ApiError(500, "UploadFailed", "Хранилище не выдало ссылку на загрузку");
  }

  const upload = await fetch(presigned.uploadUrl, {
    method: "PUT",
    // Content-Type входит в подпись ссылки и обязан совпадать с запрошенным.
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!upload.ok) {
    throw new ApiError(upload.status, "UploadFailed", "Не удалось загрузить файл");
  }

  return presigned.objectKey;
}
